import { AxiosError, AxiosResponse } from 'axios';
import {
  Candidate,
  CandidateHireDetailsResponse,
  LanguageEvaluationRating,
  LanguageEvaluationStatus,
} from '../firebase/firestore/documents/candidate';
import HttpClientWithRetry from '../http/client/HttpClientWithRetry';
import { HireFormReviewStatus, RequestHireDetailForm } from '../modules/messaging/seeker/header/actionsDropdown/HireFormTypeEnum';
import { ResubmissionRequest } from '../modules/messaging/seeker/header/seekerHireFormStatus/HireFormType';
import { TerminationReasonEnum, TerminationTypeEnum } from '../modules/messaging/seeker/header/actionsDropdown/UnhireTypeEnum';

interface Availabilities {
  availabilities: string[];
  length: number;
  fromDate: Date;
}
interface AvailablePeriods {
  availabilities: Availabilities;
}
interface Data {
  availablePeriods: AvailablePeriods;
  duration: number;
  fenceTime: number;
  type: string;
}
interface ResponseContent {
  data: Data | unknown;
  config: string | unknown;
  headers: string | unknown;
  request: string | unknown;
  status: number;
  statusText: string;
}
export type CandidateSearchResponse = {
  total: number;
  page: number;
  to: number;
  from: number;
  size: number;
  data: (Candidate & { fullName: string })[];
};
export class CandidateController {
  constructor(private readonly httpClient: HttpClientWithRetry) {}

  async sendMessage(message: string, positionId: string, seekerId: string): Promise<void> {
    const path = `/api/v1/position/${positionId}/seeker/${seekerId}/sendEmployerMessage`;
    const body = { message };
    await this.httpClient.post(path, body);
  }

  async markAsRead(positionId: string, seekerId: string): Promise<void> {
    const path = `/api/v1/position/${positionId}/seeker/${seekerId}/recruiter/markAsRead`;
    await this.httpClient.post(path, {});
  }

  async createAppointmentRequest(positionId: string, seekerId: string): Promise<AxiosResponse | undefined> {
    const path = `/api/v1/position/${positionId}/seeker/${seekerId}/createAppointmentRequest`;
    return this.httpClient
      .post(path)
      .then((response: AxiosResponse) => {
        return response;
      })
      .catch((error: AxiosError) => {
        return error.response;
      });
  }

  // Do not remove monetized and displayProfilePicture even tho the API doesn't use them, it is to keep the uri unique for caching.
  // istanbul ignore next
  async getProfilePictureUrl({ position, seeker, monetized, displayProfilePicture }: Candidate): Promise<string> {
    const path = `/api/v2/position/${position}/seeker/${seeker}/profilePicture?monetized=${monetized}&displayProfilePicture=${displayProfilePicture}`;
    const { data } = await this.httpClient.get<ArrayBuffer>(path, { responseType: 'arraybuffer' });
    const bufferArray = new Uint8Array(data);
    // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
    const stringBuffer = [].reduce.call(new Uint8Array(bufferArray), (p, c) => p + String.fromCharCode(c), '') as string;
    return `data:image/*;base64,${btoa(stringBuffer)}`;
  }

  // for new messaging due to different key for position and seeker
  // istanbul ignore next
  async getNewProfilePictureUrl({ positionId, seekerId, monetized, displayProfilePicture }: Candidate): Promise<string> {
    const path = `/api/v2/position/${positionId}/seeker/${seekerId}/profilePicture?monetized=${monetized}&displayProfilePicture=${displayProfilePicture}`;
    const { data } = await this.httpClient.get<ArrayBuffer>(path, { responseType: 'arraybuffer' });
    const bufferArray = new Uint8Array(data);
    // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
    const stringBuffer = [].reduce.call(new Uint8Array(bufferArray), (p, c) => p + String.fromCharCode(c), '') as string;
    return `data:image/*;base64,${btoa(stringBuffer)}`;
  }

  async hireCandidateRequest(
    positionId: string,
    seekerId: string,
    hireDetails: string | null,
    salary: string | null,
    salaryUnit: string,
    dateOfJoining?: Date,
    customSalaryUnit?: string | null,
    requisitionId?: string,
    updatedPositionId?: string,
    updatedBusinessId?: string,
  ): Promise<void> {
    const path = `/api/v1/position/${positionId}/seeker/${seekerId}/hire`;
    const noSalary =
      !salary ||
      parseInt(
        salary ||
          // istanbul ignore next
          '0',
        0,
      ) <= 0;
    const body = {
      hireDetails,
      offeredSalary: noSalary
        ? // istanbul ignore next
          null
        : {
            salary,
            salaryUnit,
            customSalaryUnit,
          },
      dateOfJoining,
      requisitionId,
      updatedPositionId,
      updatedBusinessId,
    };
    await this.httpClient.post(path, body);
  }

  async requestHireDetails(
    candidateId: string,
    requestHireDetailFormFields: RequestHireDetailForm,
  ): Promise<AxiosResponse | undefined> {
    const path = `/api/v1/candidates/${candidateId}/hireDetail/request`;
    return this.httpClient.post(path, requestHireDetailFormFields);
  }

  async unHireCandidateRequest(
    candidateId: string,
    accountId: string,
    blacklist: boolean,
    type: TerminationTypeEnum | '',
    reason: TerminationReasonEnum | '',
    customReason?: string,
    offboardedTime?: string,
  ): Promise<void> {
    const path = `/api/v1/account/${accountId}/candidates/${candidateId}/offboard`;
    const body = { type, reason, customReason, blacklist, offboardedTime };
    await this.httpClient.post(path, body);
  }

  async dismissCandidateRequest(
    positionId: string,
    seekerId: string,
    recruiterId: string,
    dismissalReason?: string,
    customMessage?: string,
  ): Promise<void> {
    const path = `/api/v1/position/${positionId}/seeker/${seekerId}/dismiss`;
    const body = { recruiterId, message: dismissalReason, customMessage };
    await this.httpClient.post(path, body);
  }

  async undismissCandidateRequest(positionId: string, seekerId: string, recruiterId: string): Promise<void> {
    const path = `/api/v1/position/${positionId}/seeker/${seekerId}/bookmark`;
    const body = { recruiterId };
    await this.httpClient.post(path, body);
  }

  async unblockCandidateRequest(accountId: string, candidateId: string): Promise<void> {
    const path = `/api/v1/account/${accountId}/candidates/${candidateId}/unblock`;
    await this.httpClient.post(path);
  }

  async loadAppointmentAvailabilities(positionId: string): Promise<ResponseContent> {
    const path = `/api/v1/position/${positionId}/appointmentAvailabilities`;
    return this.httpClient.get(path);
  }

  async backgroundCheckRequest(candidateId: string): Promise<AxiosResponse | undefined> {
    const path = `/api/v1/candidates/${candidateId}/screen/invite`;
    return this.httpClient
      .post(path)
      .then((response: AxiosResponse) => {
        return response;
      })
      .catch((error: AxiosError) => {
        return error.response;
      });
  }

  async getCertnPdf(candidateId: string): Promise<string> {
    const path = `/api/v1/candidates/${candidateId}/screen/report`;
    const { data } = await this.httpClient.get<string>(path, { responseType: 'blob' });
    return data;
  }

  async loadHireDetailOfCandidate(candidateId: string): Promise<AxiosResponse | undefined> {
    const path = `/api/v1/candidates/${candidateId}/hireDetail`;
    return this.httpClient
      .get(path)
      .then((response: AxiosResponse) => {
        return response;
      })
      .catch((error: AxiosError) => {
        return error.response;
      });
  }

  async loadCandidates(accountId: string): Promise<AxiosResponse | undefined> {
    const path = `api/v1/account/${accountId}/candidates/paginate`;
    return this.httpClient
      .get(path)
      .then((response: AxiosResponse) => {
        return response;
      })
      .catch((error: AxiosError) => {
        return error.response;
      });
  }

  async loadCandidatesOfPositions(accountId: string, positionIds: string[]): Promise<AxiosResponse | undefined> {
    const path = `api/v2/account/${accountId}/candidates/paginate`;
    const body = { positionIds };
    return this.httpClient
      .post(path, body)
      .then((response: AxiosResponse) => {
        return response;
      })
      .catch((error: AxiosError) => {
        return error.response;
      });
  }

  async loadCandidatesForNotifications(accountId: string, after: string, limit = 20): Promise<AxiosResponse | undefined> {
    const path = `api/v1/account/${accountId}/candidates/notifications?&limit=${limit}${after ? `&after=${after}` : ''}`;
    return this.httpClient
      .get(path)
      .then((response: AxiosResponse) => {
        return response;
      })
      .catch((error: AxiosError) => {
        return error.response;
      });
  }

  async reviewLanguageEvaluation(
    positionId: string,
    seekerId: string,
    language: string,
    phraseId: string,
    rating: LanguageEvaluationRating,
    fluency: number,
    pronunciation: number,
    accuracy: number,
    status: LanguageEvaluationStatus,
  ): Promise<void> {
    const path = `/api/v1/position/${positionId}/seeker/${seekerId}/reviewLanguageEvaluation`;
    const body = {
      language,
      phraseId,
      rating,
      fluency,
      pronunciation,
      accuracy,
      status,
    };
    await this.httpClient.post(path, body);
  }

  async requestLanguageEvaluationResubmit(phraseId: string, candidateId: string, recruiterId: string): Promise<void> {
    const path = `/api/v1/candidates//${candidateId}/recording/${phraseId}?recruiterId=${recruiterId}`;
    await this.httpClient.delete(path);
  }

  async getPositionCandidates(positionId: string): Promise<AxiosResponse | undefined> {
    const path = `/api/v2/position/${positionId}/candidates`;
    return this.httpClient
      .get(path)
      .then((response: AxiosResponse) => {
        return response;
      })
      .catch((error: AxiosError) => {
        return error.response;
      });
  }

  async saveFromDismissal(positionId: string, seekerId: string): Promise<void> {
    const path = `/api/v1/position/${positionId}/seeker/${seekerId}/saveFromDismissal`;
    await this.httpClient.post(path, {});
  }

  /* istanbul ignore next */
  async searchCandidates(
    accountId: string,
    filters: {
      searchQuery?: string;
      page?: number;
      limit?: number;
      sortBy?: 'name' | 'createdAt';
      sortOrder?: 'asc' | 'desc';
    },
    positionId?: string,
  ): Promise<AxiosResponse<CandidateSearchResponse> | undefined> {
    const path = `api/v1/account/${accountId}/candidate/search`;
    const body = { ...filters, positionId };
    const res = await this.httpClient.post<CandidateSearchResponse>(path, body);
    return res;
  }

  async getSeekerHierDetails(candidateId: string): Promise<AxiosResponse> {
    const path = `/api/v2/candidates/${candidateId}/seeker-hire-details/answer`;
    return this.httpClient.get<CandidateHireDetailsResponse>(path);
  }

  async I9CheckRequest(candidateId: string, startingDate: Date | null): Promise<AxiosResponse> {
    const path = `api/v1/candidate/${candidateId}/initializeWorkBright`;
    const body = { startingDate };
    return this.httpClient.post(path, body);
  }

  async getWorkbrightLink(candidateId: string): Promise<AxiosResponse> {
    const path = `api/v1/candidate/${candidateId}/workbright/form-link`;
    return this.httpClient.get(path);
  }

  /* istanbul ignore next */
  async reviewHireDetailsOfCandidate(
    candidateId: string,
    hireDetailsId: string,
    status: HireFormReviewStatus,
    resubmissionRequest?: ResubmissionRequest | undefined,
    rejectedReason?: string | undefined,
    blacklist?: boolean | undefined,
  ): Promise<AxiosResponse | undefined> {
    const path = `/api/v1/candidates/${candidateId}/seeker-hire-details/${hireDetailsId}/review`;
    const body: Record<string, unknown> = { status };
    if (resubmissionRequest !== undefined) body.resubmissionRequest = resubmissionRequest;
    if (rejectedReason !== undefined) body.rejectedReason = rejectedReason;
    if (blacklist !== undefined) body.blacklist = blacklist;
    return this.httpClient
      .post(path, body)
      .then((response: AxiosResponse) => {
        return response;
      })
      .catch((error: AxiosError) => {
        return error.response;
      });
  }

  async loadResubmissionFields(candidateId: string, hireDetailsId: string): Promise<AxiosResponse | undefined> {
    const path = `/api/v1/candidates/${candidateId}/seeker-hire-details/${hireDetailsId}/resubmission-fields`;
    return this.httpClient
      .get(path)
      .then((response: AxiosResponse) => {
        return response;
      })
      .catch((error: AxiosError) => {
        return error.response;
      });
  }
}
